import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listAccounts,
  type ListAccountsResponse,
  type AccountWithBalances,
  type AccountBalance,
} from '../services/accounts';

/** Format IBAN with spaces every 4 characters for readability */
function formatIban(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

/** Pick the most relevant balance to display */
function primaryBalance(balances: AccountBalance[]): AccountBalance | null {
  // Prefer closingBooked, then interimAvailable, then first available
  const preferred = ['closingBooked', 'interimAvailable', 'expected', 'openingBooked'];
  for (const type of preferred) {
    const found = balances.find((b) => b.balance_type === type);
    if (found) return found;
  }
  return balances[0] ?? null;
}

/** Human-readable balance type labels */
const balanceTypeLabels: Record<string, string> = {
  closingBooked: 'Booked',
  interimAvailable: 'Available',
  expected: 'Expected',
  openingBooked: 'Opening',
  forwardAvailable: 'Forward',
};

/** Single account card */
function AccountCard({ account }: { account: AccountWithBalances }) {
  const balance = primaryBalance(account.balances);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
      {/* Account name / product */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {account.name || account.product || 'Account'}
          </h3>
          {account.product && account.name && (
            <p className="text-xs text-gray-400 mt-0.5">{account.product}</p>
          )}
        </div>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 shrink-0">
          {account.currency}
        </span>
      </div>

      {/* IBAN */}
      {account.iban && (
        <div>
          <p className="text-xs text-gray-500">IBAN</p>
          <p className="text-sm font-mono text-gray-900 mt-0.5">
            {formatIban(account.iban)}
          </p>
        </div>
      )}

      {/* Balance */}
      {balance && (
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-gray-500">
              {balanceTypeLabels[balance.balance_type] || balance.balance_type}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              {balance.amount.toLocaleString('en', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              <span className="text-sm font-normal text-gray-500">
                {balance.currency}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* All balances (if more than one) */}
      {account.balances.length > 1 && (
        <div className="space-y-1">
          {account.balances
            .filter((b) => b !== balance)
            .map((b) => (
              <div
                key={b.balance_type}
                className="flex items-center justify-between text-xs text-gray-400"
              >
                <span>
                  {balanceTypeLabels[b.balance_type] || b.balance_type}
                </span>
                <span>
                  {b.amount.toLocaleString('en', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  {b.currency}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Status */}
      {account.status && account.status !== 'enabled' && (
        <div className="pt-2 border-t border-gray-100">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700">
            {account.status}
          </span>
        </div>
      )}
    </div>
  );
}

export default function AccountsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ListAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listAccounts();
      setData(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12">
        <svg
          className="animate-spin text-primary-600 mx-auto mb-3"
          style={{ width: '32px', height: '32px' }}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>Loading accounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">My Accounts</h1>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={fetchAccounts}
            className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const banks = data?.banks || [];
  const totalAccounts = banks.reduce((sum, b) => sum + b.accounts.length, 0);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Accounts</h1>
          <p className="mt-1 text-sm text-gray-500">
            {totalAccounts} account{totalAccounts !== 1 ? 's' : ''} across{' '}
            {banks.length} bank{banks.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/banks')}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
        >
          Connect Bank
        </button>
      </div>

      {banks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">
            No accounts yet. Connect a bank to get started.
          </p>
          <button
            onClick={() => navigate('/banks')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            Browse Banks
          </button>
        </div>
      ) : (
        banks.map((bank) => (
          <div key={bank.aspsp} className="space-y-3">
            {/* Bank header */}
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">
                {bank.bank_name}
              </h2>
              <span className="text-xs text-gray-400 font-mono">
                {bank.aspsp}
              </span>
            </div>

            {/* Account cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {bank.accounts.map((account) => (
                <AccountCard
                  key={account.resource_id}
                  account={account}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
